/**
 * Company: SPHMedia
 * Description: Filter for keyword section page
 */

import { ReactElement, useState } from "react";
import { createPortal } from "react-dom";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { Tag } from "@app/types/Cue";
import Button from "@components/AccessibleComponents/Button";
import FollowKeywordModal from "@components/FollowKeyword/FollowKeywordModal";
import { getKeywordLink } from "@components/FollowKeyword/helper";
import { faCheck, faPlus, faSpinner } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { sectionRequests } from "@pages/Section/Section.server";
import { RouteFactory } from "@src/app/routePaths";
import { MYBT_REG_LIMIT, MYBT_SUB_LIMIT } from "@src/app/util/constant";
import useMyBtStore from "@store/useMyBtStore";
import useOKTAUserStore, { OKTAUserTypeEnum } from "@store/useOKTAUserStore";
import { gaEventTracker, mySPHOpenLogin } from "@util/helpers";
import cx from "classnames";

import KeywordFilterTooltip from "./components/KeywordFilterTooltip";

export type KeywordFilterProps = {
  testId?: string;
  rootClassName?: string;
  tagClassName?: string;
  iconClassName?: string;
  tag: Tag;
  isFollowed?: boolean;
  disableTooltip?: boolean;
  disableBorder?: boolean;
  isLinkenabled?: boolean;
};

enum HttpStatusCode {
  Success = 200,
  KeywordLimitReached = 429,
}

/**
 * KeywordFilter
 * Description: Display the keyword filter in the vertical section page
 * @param props - The props required by the component (if any)
 * @returns The rendered React element
 */
export default function KeywordFilter({
  testId,
  tag,
  rootClassName,
  tagClassName,
  iconClassName,
  disableTooltip = false,
  disableBorder = false,
  isLinkenabled = true,
}: KeywordFilterProps): ReactElement {
  const [isTooltipOpen, setIsTooltipOpen] = useState(false);
  const [isModalShown, setIsModalShown] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [errorMessages, setErrorMessages] = useState(false);

  const _tag: Tag = {
    name: tag.name,
    uri: tag.uri ? tag.uri.replaceAll("keywords/", "main/") : "",
  };

  const tagDisplayName = _tag.name;
  const { pathname } = useLocation();
  const navigate = useNavigate();
  const [userType, mysphw] = useOKTAUserStore((store) => [
    store.userInfo?.usertype,
    store.userInfo?.mysphw,
  ]);

  const [addTag, minusTag, followedTags, setIsLimitModalShown] = useMyBtStore(
    (store) => [
      store.followTag,
      store.unfollowTag,
      store.tags,
      store.setIsLimitModalShown,
    ]
  );

  const follwedtag =
    pathname !== "/mybt/intro" &&
    followedTags.some(
      (followedTag) => followedTag.name.toLowerCase() === tag.name.toLowerCase()
    );

  const handleClick = () => {
    if (userType === OKTAUserTypeEnum.ANONYMOUS) {
      mySPHOpenLogin();
    }

    if (
      userType === OKTAUserTypeEnum.REGISTERED ||
      userType === OKTAUserTypeEnum.SUBSCRIBER
    ) {
      const apiToCall = follwedtag ? handleMinusTag : handleAddTag;

      apiToCall();
    }
  };

  const handleAddTag = () => {
    setIsLoading(true);
    setErrorMessages(false);
    if (
      followedTags.length ===
      (userType === OKTAUserTypeEnum.SUBSCRIBER
        ? MYBT_SUB_LIMIT
        : MYBT_REG_LIMIT)
    ) {
      setIsLimitModalShown(true);
      setIsLoading(false);
      return;
    }

    const fetchAPI = async () => {
      const response = await sectionRequests.updateMyBtFollowedKeywords(
        userType,
        mysphw ?? "",
        [tag]
      );

      if (
        response.code === HttpStatusCode.Success &&
        response.message === "SUCCESS" &&
        response.errors &&
        response.errors.length > 0
      ) {
        setErrorMessages(true);
        setIsModalShown(true);
      }

      if (
        response.code === HttpStatusCode.Success &&
        response.message == "SUCCESS" &&
        (!response.errors || response.errors.length === 0)
      ) {
        addTag(tag);
        setIsModalShown(true);
        gaEventTracker("mybt", "add", tag.name);
      }
      if (response.code === HttpStatusCode.KeywordLimitReached) {
        setIsLimitModalShown(true);
      }
      if (pathname === RouteFactory.myBTIntro)
        navigate(RouteFactory.myBTOnboard);

      setIsLoading(false);
    };

    fetchAPI();
  };

  const handleMinusTag = () => {
    setIsLoading(true);

    const fetchAPI = async () => {
      const response = await sectionRequests.deleteMyBtFollowedKeywords(
        userType,
        mysphw ?? "",
        [tag]
      );

      if (
        response.code === HttpStatusCode.Success &&
        response.message == "SUCCESS"
      ) {
        minusTag(tag);
        gaEventTracker("mybt", "remove", tag.name);
      }
      setIsLoading(false);
    };

    fetchAPI();
  };

  return (
    <div
      data-testid={testId}
      className={cx(
        rootClassName,
        pathname === RouteFactory.myBTOnboard && follwedtag
          ? "relative border-black bg-black text-white"
          : "relative",
        pathname === RouteFactory.myBTOnboard && !follwedtag
          ? "border-gray-850 bg-white"
          : ""
      )}
      onMouseEnter={() => {
        setIsTooltipOpen(true);
      }}
      onMouseLeave={() => {
        setIsTooltipOpen(false);
      }}
    >
      <div className="group">
        <div className="flex items-center">
          {isLinkenabled && tag.uri ? (
            <Link
              to={getKeywordLink(tag)}
              className={cx(
                tagClassName,
                "line-clamp-1 place-self-center break-all px-3 text-center font-poppins text-base font-semibold capitalize",
                {
                  "mr-3 border-r border-gray-450": !disableBorder,
                }
              )}
              reloadDocument
            >
              {tagDisplayName}
            </Link>
          ) : (
            <a
              href="#"
              onClick={handleClick}
              className={cx(
                tagClassName,
                "line-clamp-1 place-self-center break-all px-3 text-center font-poppins text-base font-semibold capitalize",
                {
                  "mr-3 border-r border-gray-450": !disableBorder,
                }
              )}
            >
              {tagDisplayName}
            </a>
          )}
          <Button
            onPress={handleClick}
            className="ml-auto leading-none outline-none"
          >
            <FontAwesomeIcon
              icon={isLoading ? faSpinner : follwedtag ? faCheck : faPlus}
              spin={isLoading}
              size="sm"
              color="text-gray-850"
              className={iconClassName}
            />
          </Button>
        </div>

        {isTooltipOpen && !disableTooltip ? <KeywordFilterTooltip /> : null}
      </div>
      {isModalShown && (follwedtag || errorMessages)
        ? createPortal(
            <FollowKeywordModal hasError={errorMessages} />,
            document.body
          )
        : null}
    </div>
  );
}
